import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import CloseIconButton from 'Src/components/buttons/CloseIconButton';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import TitleItems from 'Src/components/TitleItems';
import { useEmployee } from 'Src/feature/employee/useEmployee';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { UsedModalSearchParamControl, useModalSearchParamControl } from 'Src/feature/modal/useModalControl';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { usePrinter } from 'Src/feature/printer/usePrinter';

import { PayInOutTx } from './payInOutService';
import { PayInOutVoidConfirmation } from './PayInOutVoidConfirmation';
import { usePayInOut } from './usePayInOut';

const sectionId = 'PayInOutDetails';

interface PayInOutDetailsProps {
  payInOutDetailsModal: UsedModalSearchParamControl;
  payInOutTx?: PayInOutTx;
}

const PayInOutDetails: FC<PayInOutDetailsProps> = ({ payInOutDetailsModal, payInOutTx }) => {
  const { t } = useTranslation();

  const { data: paymentMethodMap } = usePaymentMethod.query.map();
  const { data: payInOutReasonMap } = usePayInOut.query.reason.map();
  const employeeGetAllMap = useEmployee.query.map();
  const printerSend = usePrinter.mutate.send();

  const payInOutVoidConfirmModal = useModalSearchParamControl({ value: 'pay-in-out-void-confirm' });

  const printPayInReceipt = async () => await printerSend.mutateAsync({ type: 'Payment', payInOutTxId: payInOutTx?.id });
  const onClose = () => payInOutDetailsModal.close();
  const onVoid = () => payInOutVoidConfirmModal.open();
  const onPrint = () => void printPayInReceipt();

  const employee = payInOutTx?.employeeId ? employeeGetAllMap.data?.get(payInOutTx?.employeeId) : undefined;

  const isDeclined = payInOutTx?.status === 'Declined';

  const declinedLabel = isDeclined && (
    <Flex horizontal alignItems="center" justifyContent="center">
      <Copy value={t('payInOut.labels.payInDeclined')} />
    </Flex>
  );

  return (
    <>
      <Modal
        data-testid={sectionId}
        open={payInOutDetailsModal.isOpen}
        onBackDropClick={onClose}
      >
        <ModalHeader closeButton={<CloseIconButton onClick={onClose} />}>
          <ModalTitle copy={payInOutTx?.isPayIn ? t('payInOut.labels.payInDetails') : t('payInOut.labels.payOutDetails')} />
        </ModalHeader>

        <ModalContent>
          <TitleItems
            items={[
              { primary: t('labels.type'),
                secondary: (
                  <Flex horizontal gap="xs" alignItems="center">
                    <FontIcon name="fiber_manual_record" color={payInOutTx?.isPayIn ? 'success' : 'error'} />
                    <Copy value={payInOutTx?.isPayIn ? t('payInOut.labels.payIn') : t('payInOut.labels.payOut')} />
                  </Flex>
                ) },
              { primary: t('labels.time'), secondary: <Copy value={payInOutTx?.createdAt ? DateTime.fromMillis(payInOutTx?.createdAt)?.toLocaleString(DateTime.TIME_SIMPLE) : ''} /> },
              { primary: t('labels.amount'), secondary: <Copy type="currency" value={payInOutTx?.amount} /> },
              { primary: t('employee.label'), secondary: <Copy value={employee ? t('labels.util.employeeFullName', employee) : undefined} /> },
              { primary: t('labels.method'), secondary: <Copy value={paymentMethodMap?.get(payInOutTx?.paymentMethodId as number)?.name} /> },
              { primary: t('labels.reason'), secondary: <Copy value={payInOutReasonMap?.get(payInOutTx?.payInOutReasonId as number)?.name} /> },
            ]}
          />
        </ModalContent>
        {declinedLabel}
        <ModalActions>
          {payInOutTx?.isVoid ? (
            <ModalAction
              data-testid={`${sectionId}-cancel-btn`}
              onClick={onClose}
              color="secondary"
              label={t('labels.cancel')}
            />
          ) : (
            <ModalAction
              data-testid={`${sectionId}-void-btn`}
              onClick={onVoid}
              disabled={payInOutTx?.isVoid || isDeclined}
              color="secondary"
              label={payInOutTx?.isPayIn ? t('payInOut.labels.voidPayIn') : t('payInOut.labels.voidPayOut')}
            />
          )}
          <ModalAction
            data-testid={`${sectionId}-print-btn`}
            onClick={onPrint}
            color="primary"
            label={payInOutTx?.isPayIn ? t('payInOut.labels.printPayIn') : t('payInOut.labels.printPayOut')}
            disabled = {isDeclined}
          />
        </ModalActions>
      </Modal>
      {payInOutTx && (
        <PayInOutVoidConfirmation 
          payInOutDetailModal={payInOutDetailsModal}
          payInOutVoidConfirmModal={payInOutVoidConfirmModal}
          payInOutTx={payInOutTx} />
      )}
    </>
  );
};

export default PayInOutDetails;
export { sectionId };
